/** Gedeeld wire-contract voor de handmatige pagina en het inboxverb. */
export type ProductNaleveringBron = 'handmatig' | 'inbox';

export interface ProductNaleveringMaat {
  width: number;
  length: number;
}

export interface ProductNaleveringSelectie {
  /** Exacte WI-referentie op de oorspronkelijke bestelling. */
  artikel_referentie: string;
  /** Concrete magazijn-SKU. Verplicht zodra de verkoopregel meerdere componenten heeft. */
  fulfilmentproduct_code?: string;
  /** Ontbrekend aantal van de verkochte component, geen m2. */
  aantal: number;
  /** Verplichte snijmaat voor een maatwerk-fulfilmentcomponent. */
  custom_size?: ProductNaleveringMaat;
}

export interface HandmatigeProductNalevering {
  bron_order_referentie: string;
  selecties: ProductNaleveringSelectie[];
  /** Browser-gegenereerde UUID; blijft gelijk bij retry/dubbelklik. */
  incident_id: string;
  /** Alleen na een expliciete keuze uit de serverpreview. */
  bestaande_order_referentie?: string;
}

export interface BestaandeProductNalevering {
  orderId: string;
  nummer: string;
  werkOrderStatus: string;
  verzendStatus: string | null;
  trackingCode: string | null;
  trackingUrl: string | null;
  componenten: Array<{
    fulfilmentproductCode: string;
    componentnaam: string;
    aantal: number;
    customSize?: ProductNaleveringMaat;
  }>;
}

export type ProductNaleveringCopyStatus =
  | 'wachtrij'
  | 'aangemeld'
  | 'opgehaald'
  | 'tracking'
  | 'controle_nodig';

export interface ProductNaleveringCopy {
  status: ProductNaleveringCopyStatus;
  tekst: string;
}

/**
 * Klantcopy volgt uitsluitend aantoonbare fulfilmentstaat. `Aangemeld` is nog
 * niet door PostNL opgehaald; zonder trackingbewijs claimen we dat nooit.
 */
export function productNaleveringStatusCopy(input: {
  artikel: string;
  werkOrderStatus?: string | null;
  verzendStatus?: string | null;
  trackingCode?: string | null;
  trackingUrl?: string | null;
}): ProductNaleveringCopy {
  const rauwArtikel = input.artikel.trim() || 'artikel';
  const artikel = /^(de|het|een)\s/i.test(rauwArtikel)
    ? rauwArtikel
    : /^(artikel|ondertapijt|pakket|product)\b/i.test(rauwArtikel)
      ? `het ${rauwArtikel}`
      : `de ${rauwArtikel}`;
  const code = input.trackingCode?.trim() || null;
  const url = input.trackingUrl?.trim() || null;
  if (code || url) {
    const bewijs = code && url
      ? `met track-en-tracecode ${code}: ${url}`
      : code
        ? `met track-en-tracecode ${code}`
        : `via ${url}`;
    return { status: 'tracking', tekst: `We sturen ${artikel} kosteloos na via PostNL. Je kunt het pakket volgen ${bewijs}.` };
  }
  if ((input.verzendStatus || '').trim() === 'Aangemeld') {
    return {
      status: 'aangemeld',
      tekst: `We sturen ${artikel} kosteloos na via PostNL. Zodra PostNL het pakket heeft opgehaald, ontvang je per e-mail de track-en-tracecode.`,
    };
  }
  if (['Opgehaald', 'Onderweg'].includes((input.verzendStatus || '').trim())) {
    return {
      status: 'opgehaald',
      tekst: `We hebben ${artikel} kosteloos nagezonden via PostNL. Het pakket is door PostNL opgehaald en onderweg.`,
    };
  }
  if ((input.verzendStatus || '').trim() === ''
    && (input.werkOrderStatus || '').trim() === 'Klaarmaken verzending') {
    return { status: 'wachtrij', tekst: `We zetten ${artikel} kosteloos klaar voor verzending via PostNL.` };
  }
  return {
    status: 'controle_nodig',
    tekst: `De status van de nalevering van ${artikel} moet eerst worden gecontroleerd.`,
  };
}

export interface ProductNaleveringBewijs {
  orderId:string;
  nummer:string;
  status:'klaargezet';
  bronOrder:string;
  bestaand?: boolean;
  klantcopy?: ProductNaleveringCopy;
}

export function leesProductNalevering(v:unknown):ProductNaleveringBewijs|null {
  if(!v||typeof v!=='object')return null;
  const p=v as Record<string,unknown>;
  return typeof p.orderId==='string'&&/^(eigen-[0-9a-f-]{36}|[a-zA-Z0-9]{15,18})$/.test(p.orderId)
    &&typeof p.nummer==='string'&&/^WB-\d+$/.test(p.nummer)&&p.status==='klaargezet'
    &&typeof p.bronOrder==='string'&&/^WB-\d+$/.test(p.bronOrder)?p as unknown as ProductNaleveringBewijs:null;
}
